import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";

const STATUS_STYLES = {
  Active:  { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
  Pending: { bg: "#fffbeb", color: "#92400e", border: "#fde68a" },
  Expired: { bg: "#f5f5f5", color: "#6b7280", border: "#e5e7eb" },
  Removed: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca" },
};

export default function AdminView({ onBack, onNewRequest }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [confirmItem, setConfirmItem] = useState(null);
  const [expiring, setExpiring] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getApiToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${API_BASE_URL}/travel/allRequests`, {
        params: { adminUPN: user.username },
        headers
      });
      setRequests(res.data.requests || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load requests.");
    } finally {
      setLoading(false);
    }
  }, [user.username, instance]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleForceExpire = async () => {
    if (!confirmItem) return;
    setExpiring(true);
    try {
      const token = await getApiToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`${API_BASE_URL}/travel/forceExpire`, {
        itemId: confirmItem._itemId,
        userUPN: confirmItem.UserUPN,
        securityGroupId: confirmItem.SecurityGroupId,
        capId: confirmItem.CAPId,
        namedLocationId: confirmItem.NamedLocationId,
        adminUPN: user.username
      }, { headers });

      setSuccessMsg(`Successfully force-expired ${confirmItem.Country} request for ${confirmItem.UserUPN}`);
      setConfirmItem(null);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || "Force expire failed.");
      setConfirmItem(null);
    } finally {
      setExpiring(false);
    }
  };

  const filtered = requests.filter(r => {
    const matchStatus = filter === "All" || r.Status === filter;
    const matchSearch = !search ||
      r.UserUPN?.toLowerCase().includes(search.toLowerCase()) ||
      r.UserDisplayName?.toLowerCase().includes(search.toLowerCase()) ||
      r.Country?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const counts = {
    All: requests.length,
    Active: requests.filter(r => r.Status === "Active").length,
    Pending: requests.filter(r => r.Status === "Pending").length,
    Expired: requests.filter(r => r.Status === "Expired").length,
    Removed: requests.filter(r => r.Status === "Removed").length,
  };

  return (
    <div className="page">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#230658" />
              <path d="M9 16L14 21L23 11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Travel Request Portal</span>
          </div>
          <div className="header-right">
            <span className="admin-badge">Admin</span>
            <button className="btn-nav" onClick={onNewRequest}>+ New request</button>
            <div className="header-user">
              <div className="user-avatar">{user.name ? user.name[0].toUpperCase() : "A"}</div>
              <span>{user.name}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="requests-title-row">
          <div>
            <h2 className="page-title">Admin — All travel requests</h2>
            <p className="page-subtitle">Viewing as {user.username}</p>
          </div>
          <button className="btn-secondary" onClick={onBack}>← Back</button>
        </div>

        <div className="admin-search-row">
          <input
            type="text"
            placeholder="Search by user, name, or country..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="admin-search"
          />
          <button className="btn-secondary" onClick={fetchAll}>Refresh</button>
        </div>

        <div className="filter-tabs">
          {["All", "Active", "Pending", "Expired", "Removed"].map((f) => (
            <button key={f} className={`filter-tab ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
              {f}<span className="filter-count">{counts[f]}</span>
            </button>
          ))}
        </div>

        {successMsg && <div className="success-banner" onClick={() => setSuccessMsg(null)}>✓ {successMsg}</div>}
        {error && <div className="error-banner">{error}</div>}
        {loading && <div className="requests-loading"><div className="spinner" /><p>Loading all travel requests...</p></div>}
        {!loading && filtered.length === 0 && <div className="empty-state"><p>No {filter !== "All" ? filter.toLowerCase() : ""} requests found.</p></div>}

        {!loading && filtered.length > 0 && (
          <div className="requests-list">
            {filtered.map((req, i) => {
              const style = STATUS_STYLES[req.Status] || STATUS_STYLES.Active;
              const canExpire = req.Status === "Active" || req.Status === "Pending";
              return (
                <div key={i} className={`request-card ${!canExpire ? "faded" : ""}`}>
                  <div className="request-card-header">
                    <div className="request-country-row">
                      <div className="trip-code-badge sm">{req.Country?.slice(0, 2).toUpperCase()}</div>
                      <strong className="request-country">{req.Country}</strong>
                      <span className="status-badge" style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}>
                        {req.Status}
                      </span>
                      {req.AdminBypass && <span className="admin-bypass-badge" title={req.AdminBypass}>Admin expired</span>}
                    </div>
                    {canExpire && (
                      <button className="btn-force-expire" onClick={() => setConfirmItem(req)}>Force expire</button>
                    )}
                  </div>

                  <div className="admin-user-row">
                    <div className="user-avatar sm">{req.UserDisplayName ? req.UserDisplayName[0].toUpperCase() : "U"}</div>
                    <div>
                      <span className="admin-user-name">{req.UserDisplayName}</span>
                      <span className="admin-user-upn">{req.UserUPN}</span>
                    </div>
                    {req.SubmittedBy && req.SubmittedBy !== req.UserUPN && (
                      <span className="admin-bypass-badge" title={`Submitted by ${req.SubmittedBy}`}>On behalf</span>
                    )}
                  </div>

                  <div className="request-dates-row">
                    <div className="request-date-block">
                      <span className="date-label">Departure</span>
                      <span className="date-value">{formatDate(req.TravelStartDate)}</span>
                    </div>
                    <div className="date-divider">→</div>
                    <div className="request-date-block">
                      <span className="date-label">Return</span>
                      <span className="date-value">{formatDate(req.TravelEndDate)}</span>
                    </div>
                    <div className="request-duration">{getDuration(req.TravelStartDate, req.TravelEndDate)} days</div>
                  </div>

                  <div className="request-group-row">
                    <span className="group-label">Security group</span>
                    <code className="group-value">{req.SecurityGroup || req.SecurityGroupId}</code>
                  </div>

                  {req.AdminBypass && (
                    <div className="admin-bypass-row">
                      <span className="group-label">Admin bypass</span>
                      <span className="bypass-value">{req.AdminBypass}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {confirmItem && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3>Force expire this request?</h3>
            <p>This will immediately remove <strong>{confirmItem.UserDisplayName}</strong> from the security group and mark the request as expired.</p>
            <div className="modal-detail">
              <div><span className="group-label">User</span><span>{confirmItem.UserUPN}</span></div>
              <div><span className="group-label">Country</span><span>{confirmItem.Country}</span></div>
              <div><span className="group-label">Group</span><code className="group-value">{confirmItem.SecurityGroup || confirmItem.SecurityGroupId}</code></div>
              <div><span className="group-label">Initiated by</span><span>{user.username}</span></div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setConfirmItem(null)} disabled={expiring}>Cancel</button>
              <button className="btn-danger" onClick={handleForceExpire} disabled={expiring}>
                {expiring ? <><span className="btn-spinner" /> Expiring...</> : "Yes, force expire"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getDuration(start, end) {
  if (!start || !end) return "—";
  const diff = new Date(end) - new Date(start);
  return Math.max(1, Math.round(diff / (1000 * 60 * 60 * 24)));
}